
import { ArrowRight, MapPin, Camera, Shield, Star, CheckCircle, DollarSign, Phone, Clock, Users, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";

const DiasporaSection = () => {
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  const projectTypes = [
    {
      icon: "🏠",
      title: "Building Projects",
      description: "Houses, shops, boundary walls - track every brick laid from anywhere in the world"
    },
    {
      icon: "🐐",
      title: "Livestock Farming",
      description: "Cattle, goats, chickens - monitor your herds with daily updates and health reports"
    },
    {
      icon: "☀️",
      title: "Solar Installations",
      description: "Solar panels, batteries, inverters - ensure proper installation and maintenance"
    },
    {
      icon: "💧",
      title: "Borehole Projects",
      description: "Water drilling, pump installation - verify depth and water quality remotely"
    }
  ];

  const paymentMethods = [
    {
      name: "Mukuru",
      description: "Direct integration with Mukuru for seamless remittances",
      status: "Coming Soon",
      flag: "🇿🇼"
    },
    {
      name: "WorldRemit", 
      description: "Global money transfers made simple and trackable",
      status: "Coming Soon",
      flag: "🌍"
    },
    {
      name: "EcoCash",
      description: "Mobile money payments directly to suppliers in Zimbabwe",
      status: "Coming Soon", 
      flag: "📱"
    },
    {
      name: "USD Cash",
      description: "US Dollar payments with verified receipt tracking",
      status: "Available",
      flag: "💵"
    }
  ];

  const proofFeatures = [
    {
      icon: Camera,
      title: "Before/After Photos",
      description: "Timestamped photos showing progress at every milestone"
    },
    {
      icon: MapPin,
      title: "GPS Geo-tagging", 
      description: "Location verification ensures work is happening where it should"
    },
    {
      icon: Shield,
      title: "Verified Receipts",
      description: "All purchases documented with supplier verification"
    },
    {
      icon: Star,
      title: "Supplier Ratings",
      description: "Community-verified ratings from other diaspora families"
    }
  ];

  const premiumFeatures = [
    "📸 Daily photo updates",
    "⚡ Priority task execution", 
    "📲 Direct WhatsApp support",
    "🎯 Dedicated project manager",
    "📊 Weekly progress reports",
    "🔒 Enhanced security monitoring"
  ];

  const testimonials = [
    {
      name: "Sarah Mutindi",
      location: "Manchester, UK 🇬🇧",
      project: "House Building in Harare",
      quote: "Finally built my dream home in Borrowdale without stepping foot in Zimbabwe. The daily photos gave me complete peace of mind.",
      rating: 5,
      image: "/placeholder.svg"
    },
    {
      name: "Trevor Chikwanha", 
      location: "Dallas, USA 🇺🇸",
      project: "Cattle Farming in Masvingo",
      quote: "My 50 cattle herd is thriving! GPS tracking and health reports help me manage everything from Texas.",
      rating: 5,
      image: "/placeholder.svg"
    },
    {
      name: "Memory Ncube",
      location: "Toronto, Canada 🇨🇦", 
      project: "Solar Installation in Bulawayo",
      quote: "Solar panels installed perfectly. The technical verification reports were exactly what I needed.",
      rating: 5,
      image: "/placeholder.svg"
    }
  ];

  const faqs = [
    {
      question: "How do I know the work is really done?",
      answer: "Every project milestone includes timestamped photos, GPS verification, and third-party confirmation. Our agents physically visit and document progress. You can also request video calls for real-time verification."
    },
    {
      question: "Can I get a refund if work isn't completed?",
      answer: "Yes! We offer milestone-based payments with escrow protection. If work doesn't meet agreed standards, funds are held until resolution. Our dispute resolution process protects diaspora investments."
    },
    {
      question: "Can I use EcoCash or Mukuru for payments?",
      answer: "Payment integrations with Mukuru, WorldRemit, and EcoCash are coming soon! Currently, we support USD cash payments with full receipt tracking. These integrations will launch in early 2025."
    },
    {
      question: "What if my local worker disappears with my money?",
      answer: "All workers are pre-verified with ID checks, references, and community ratings. We use milestone payments and require photo proof before releasing funds. Plus, our local agents monitor all projects."
    },
    {
      question: "How much does ZimKonnect cost?",
      answer: "Basic tracking is free. Premium features (daily photos, WhatsApp support, priority execution) start at $29/month. No setup fees, cancel anytime."
    }
  ];

  const handleGetStarted = () => {
    document.getElementById('join-waitlist')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="py-16 sm:py-24 bg-gradient-to-br from-amber-50 via-orange-50 to-red-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <span className="text-lg">🇿🇼</span>
            For Zimbabwe Diaspora
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Track Your Zimbabwe Projects
            <span className="block bg-gradient-to-r from-amber-600 to-red-600 bg-clip-text text-transparent">
              From Anywhere in the World
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Whether you're in London, Dallas, or Sydney - monitor your investments back home with complete transparency and accountability.
          </p>
        </div>

        {/* Project Types */}
        <div className="mb-20">
          <h3 className="text-2xl sm:text-3xl font-bold text-center mb-12">What You Can Track Remotely</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {projectTypes.map((project, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-shadow border border-gray-100">
                <div className="text-4xl mb-4 text-center">{project.icon}</div>
                <h4 className="text-lg font-semibold text-gray-900 mb-3 text-center">{project.title}</h4>
                <p className="text-gray-600 text-sm text-center leading-relaxed">{project.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Hiring Local Workers */}
        <div className="mb-20 bg-white rounded-3xl p-8 sm:p-12 shadow-xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6">
                Hire Trusted Local Workers
              </h3>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-gray-900">Verified Professionals</p>
                    <p className="text-gray-600 text-sm">Builders, goat herders, tutors, mechanics - all ID-verified with community ratings</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-gray-900">Reference Checked</p>
                    <p className="text-gray-600 text-sm">Previous work history and diaspora family testimonials available</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-gray-900">Local Monitoring</p>
                    <p className="text-gray-600 text-sm">Our agents visit regularly to ensure work quality and timeline adherence</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1472396961693-142e6e269027?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Zimbabwe rural workers and projects"
                className="rounded-2xl shadow-lg w-full h-80 object-cover"
              />
              <div className="absolute top-4 right-4 bg-green-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                ✓ Verified
              </div>
            </div>
          </div>
        </div>

        {/* Payment Methods */}
        <div className="mb-20">
          <h3 className="text-2xl sm:text-3xl font-bold text-center mb-12">Diaspora-Friendly Payment Methods</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {paymentMethods.map((method, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 relative">
                <div className="text-2xl mb-4">{method.flag}</div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">{method.name}</h4>
                <p className="text-gray-600 text-sm mb-4">{method.description}</p>
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                  method.status === 'Available' 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-amber-100 text-amber-800'
                }`}>
                  {method.status}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Proof System */}
        <div className="mb-20 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-3xl p-8 sm:p-12">
          <h3 className="text-2xl sm:text-3xl font-bold text-center mb-12">Complete Transparency & Proof</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {proofFeatures.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-3">{feature.title}</h4>
                <p className="text-gray-600 text-sm">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Premium Features */}
        <div className="mb-20 bg-gradient-to-r from-amber-600 to-red-600 rounded-3xl p-8 sm:p-12 text-white">
          <div className="text-center mb-12">
            <h3 className="text-2xl sm:text-3xl font-bold mb-4">Diaspora Premium Benefits</h3>
            <p className="text-xl text-amber-100">Enhanced monitoring for serious investors</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {premiumFeatures.map((feature, index) => (
              <div key={index} className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-xl p-4">
                <CheckCircle className="w-5 h-5 text-yellow-300 flex-shrink-0" />
                <span className="text-amber-100">{feature}</span>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Button 
              onClick={handleGetStarted}
              className="bg-white text-amber-600 hover:bg-amber-50 px-8 py-4 text-lg font-semibold rounded-2xl shadow-lg"
            >
              Upgrade to Premium
            </Button>
          </div>
        </div>

        {/* CTA Buttons */}
        <div className="mb-20">
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center max-w-4xl mx-auto">
            <Button 
              onClick={handleGetStarted}
              className="w-full sm:w-auto bg-gradient-to-r from-amber-600 to-red-600 text-white px-8 py-6 text-lg font-semibold rounded-2xl hover:from-amber-700 hover:to-red-700 shadow-xl flex items-center justify-center gap-2"
            >
              <span>Start My Project</span>
              <ArrowRight className="w-5 h-5" />
            </Button>
            <Button 
              onClick={handleGetStarted}
              className="w-full sm:w-auto bg-blue-600 text-white px-8 py-6 text-lg font-semibold rounded-2xl hover:bg-blue-700 shadow-xl flex items-center justify-center gap-2"
            >
              <Users className="w-5 h-5" />
              <span>Hire Someone Back Home</span>
            </Button>
            <Button 
              onClick={handleGetStarted}
              className="w-full sm:w-auto bg-green-600 text-white px-8 py-6 text-lg font-semibold rounded-2xl hover:bg-green-700 shadow-xl flex items-center justify-center gap-2"
            >
              <DollarSign className="w-5 h-5" />
              <span>Track My Investment</span>
            </Button>
          </div>
        </div>

        {/* Testimonials */}
        <div className="mb-20">
          <h3 className="text-2xl sm:text-3xl font-bold text-center mb-12">Success Stories from the Diaspora</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                <div className="flex items-center mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 mb-6 italic">"{testimonial.quote}"</p>
                <div className="flex items-center gap-3">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-semibold text-gray-900">{testimonial.name}</p>
                    <p className="text-sm text-gray-500">{testimonial.location}</p>
                    <p className="text-xs text-blue-600">{testimonial.project}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQ Section */}
        <div className="bg-white rounded-3xl p-8 sm:p-12 shadow-xl">
          <h3 className="text-2xl sm:text-3xl font-bold text-center mb-12">Frequently Asked Questions</h3>
          <div className="space-y-4 max-w-4xl mx-auto">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-xl">
                <button
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                  className="w-full text-left p-6 flex justify-between items-center hover:bg-gray-50 transition-colors"
                >
                  <span className="font-semibold text-gray-900 pr-4">{faq.question}</span>
                  <div className={`transition-transform ${expandedFaq === index ? 'rotate-180' : ''}`}>
                    <ArrowRight className="w-5 h-5 text-gray-400 rotate-90" />
                  </div>
                </button>
                {expandedFaq === index && (
                  <div className="px-6 pb-6">
                    <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default DiasporaSection;
